import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RSVPForm from './RSVPForm';
import { GuestGroup, ModalState } from '../types/interfaces';
import { FaBus, FaHeartBroken } from 'react-icons/fa';
import { GiPartyPopper } from 'react-icons/gi';
import confetti from 'canvas-confetti';

interface ModalProps {
    modalState: ModalState;
    error: boolean;
    guestGroup: GuestGroup | null;
    onClose: () => void;
    onRSVPComplete: (updatedGroup: GuestGroup) => void;
}

// Reusable Close Button Component
const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t } = useTranslation();
    return (
        <button
            onClick={onClick}
            className="w-full bg-[#2d5a2d] text-white py-2.5 px-4 rounded-md hover:bg-[#245024] font-body font-medium transition-colors duration-200"
        >
            {t('close')}
        </button>
    );
};

// Reusable Guest List Component
const GuestList: React.FC<{ guestGroup: GuestGroup }> = ({ guestGroup }) => {
    const { t } = useTranslation();
    const hasBus = guestGroup.needsBus === true;
    return (
        <div className="space-y-2">
            {guestGroup.guests.map((guest, index) => (
                <div key={index} className="bg-[#faf8f5] px-3 py-2.5 border border-[#e8e4dc] rounded-md">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-body text-base font-medium text-[#2d5a2d]">{guest.fullName}</span>
                        {guest.coming ? (
                            <span className="px-4 py-1.5 text-sm font-body font-medium rounded-md border bg-[#2d5a2d] text-white border-[#2d5a2d]">
                                {t('yes')}
                            </span>
                        ) : (
                            <span className="px-4 py-1.5 text-sm font-body font-medium rounded-md border bg-[#9c7b6b] text-white border-[#9c7b6b]">
                                {t('no')}
                            </span>
                        )}
                    </div>
                </div>
            ))}
            {guestGroup.guests.some(g => g.coming) && (
                <div className="bg-[#faf8f5] px-3 py-2.5 border border-[#e8e4dc] rounded-md">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FaBus className="text-[#2d5a2d] w-4 h-4" />
                            <span className="font-body text-sm font-medium text-[#2d5a2d]">{t('shuttle')}</span>
                        </div>
                        {hasBus ? (
                            <span className="px-4 py-1.5 text-sm font-body font-medium rounded-md border bg-[#2d5a2d] text-white border-[#2d5a2d]">
                                {t('yes')}
                            </span>
                        ) : (
                            <span className="px-4 py-1.5 text-sm font-body font-medium rounded-md border bg-[#9c7b6b] text-white border-[#9c7b6b]">
                                {t('no')}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Modal: React.FC<ModalProps> = ({
    modalState,
    error,
    guestGroup,
    onClose,
    onRSVPComplete
}) => {
    const { t } = useTranslation();

    // Trigger confetti when Thank You modal is shown
    useEffect(() => {
        if (modalState === ModalState.THANK_YOU && guestGroup) {
            const atLeastOneComing = guestGroup.guests.some(guest => guest.coming);
            if (atLeastOneComing) {
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 },
                    colors: ['#FFD700', '#FF69B4', '#87CEEB', '#98FB98', '#DDA0DD']
                });
            }
        }
    }, [modalState, guestGroup]);

    const renderModalContent = () => {
        switch (modalState) {
            case ModalState.ERROR:
                return (
                    <div className="flex flex-col items-center">
                        <p className="font-body text-gray-600 text-center mb-4 text-sm">
                            {t('error_message')}
                        </p>
                        <CloseButton onClick={onClose} />
                    </div>
                );

            case ModalState.RSVP:
                return guestGroup ? (
                    <RSVPForm
                        guestGroup={guestGroup}
                        onRSVPComplete={onRSVPComplete}
                        onClose={onClose}
                    />
                ) : null;

            case ModalState.THANK_YOU:
                return (
                    <div className="flex flex-col">
                        <div className="text-center mb-6">
                            <h1 className="font-playfair text-2xl font-medium text-[#2d5a2d] mb-2">
                                {t('thank_you_title')} <GiPartyPopper className="inline text-[#c9a96e] w-5 h-5" />
                            </h1>
                            <p className="font-body text-sm font-medium text-gray-600">
                                {t('look_forward')}
                            </p>
                        </div>

                        <div className="space-y-3 mb-5">
                            {guestGroup && <GuestList guestGroup={guestGroup} />}
                        </div>

                        <div className="flex gap-3 pt-3">
                            <CloseButton onClick={onClose} />
                        </div>
                    </div>
                );

            case ModalState.SORRY:
                return (
                    <div className="flex flex-col">
                        <div className="text-center mb-6">
                            <h1 className="font-playfair text-2xl font-medium text-[#2d5a2d] mb-2">
                                {t('miss_you')} <FaHeartBroken className="inline text-[#9c7b6b] w-5 h-5" />
                            </h1>
                            <p className="font-body text-sm font-medium text-gray-600">
                                {t('sorry_message')}
                            </p>
                        </div>

                        <div className="flex gap-3 pt-3">
                            <CloseButton onClick={onClose} />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    if (modalState === ModalState.NONE) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-20 pt-12 backdrop-blur-sm">
            <div className="bg-[#fffdf9] p-5 w-full mx-3 max-w-sm max-h-[calc(100vh-6rem)] overflow-y-auto flex flex-col rounded-lg shadow-lg border border-[#e8e4dc]">
                {renderModalContent()}
            </div>
        </div>
    );
};

export default Modal;
